import Component from '@glimmer/component';
import safeFormatDate from '../../../../helpers/safe-format-date.ts';
import Button from 'tio-reimagined/components/button';
import { enqueueTask } from 'ember-concurrency';
import { eq } from 'tio-ui/utilities';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import Popover from 'tio-reimagined/components/popover';
import { t } from 'ember-intl';
import {
  PhFile,
  PhFileCsv,
  PhFileDoc,
  PhFileImage,
  PhFileJpg,
  PhFilePdf,
  PhFilePng,
  PhFilePpt,
  PhFileText,
  PhFileTxt,
  PhFileXls,
  PhFileZip,
  PhInfo,
  PhXCircle,
} from 'ember-phosphor-icons';
import Table from 'tio-reimagined/components/table';
import type TasAssetModel from '../../../../models/tas-asset.ts';

interface UploadAttachmentsAttachmentListSignature {
  Args: {
    assets: TasAssetModel[] | readonly TasAssetModel[];
    disabled?: boolean;
    hideDiscarded?: boolean;
  };
  Element: HTMLElement;
}

export default class TuitionAssistanceFormsUploadAttachmentsAttachmentListComponent extends Component<UploadAttachmentsAttachmentListSignature> {
  removeAsset = enqueueTask(async (asset) => {
    const attachment = asset.attachments[0];
    if (!attachment) {
      console.warn('No attachment found for asset', asset);
      return;
    }
    await attachment.destroyRecord();
    await asset.reload();
  });

  get sortedAssets(): TasAssetModel[] {
    const assets = this.args.assets ?? [];

    const notDeleted = assets
      .filter((a) => a.attachmentDeletedAt === null)
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

    if (this.args.hideDiscarded) {
      return notDeleted;
    }

    const deleted = assets.filter((a) => a.attachmentDeletedAt !== null);

    return [...notDeleted, ...deleted];
  }

  removeAssetAction = (asset: TasAssetModel) => {
    try {
      this.removeAsset?.perform(asset);
    } catch (error) {
      console.error(error);
    }
  };

  classificationString = (classification: string) => {
    const classificationMap: Record<string, string> = {
      COMPLETION_DOCUMENTS: 'Final Approval Document',
      COURSE_APPROVAL_DOCUMENTS: 'Approval Document',
      PROGRAM_APPROVAL_DOCUMENTS: 'Program Approval Document',
      APPLICATION_LIFECYCLE_DOCUMENTS: 'Approval Document (Admin)',
      PROGRAM_COMPLETION_DOCUMENTS: 'Program Completion Document',
    };

    return classificationMap[classification] || '';
  };
  disableAction = (asset: TasAssetModel) => {
    return this.args.disabled || asset.attachmentDeletedAt !== null;
  };

  <template>
    <Table ...attributes>
      <:thead as |options|>
        {{#if this.sortedAssets.length}}
          <options.tr>
            <options.th class="w-2/5">{{t "common.tas.attachments.file_name"}}</options.th>
            <options.th class="w-1/4">{{t "common.tas.attachments.file_type"}}</options.th>
            <options.th class="w-1/5">{{t "common.tas.attachments.upload_date"}}</options.th>
            <options.th class="w-[60px]" />
          </options.tr>
        {{/if}}
      </:thead>

      <:tbody as |options|>
        {{#each this.sortedAssets as |asset|}}
          <options.tr class="even:bg-tio-gray-50 dark:even:bg-gray-800">
            <options.td class="w-2/5">
              <div
                class="flex items-start gap-x-2
                  {{if asset.attachmentDeletedAt 'line-through text-tio-gray-400'}}"
              >

                {{#if (eq asset.extension "pdf")}}
                  <PhFilePdf @size="24" class="shrink-0 mt-0.5" />
                {{else if (eq asset.extension "doc")}}
                  <PhFileDoc @size="24" class="shrink-0 mt-0.5" />
                {{else if (eq asset.extension "xls")}}
                  <PhFileXls @size="24" class="shrink-0 mt-0.5" />
                {{else if (eq asset.extension "ppt")}}
                  <PhFilePpt @size="24" class="shrink-0 mt-0.5" />
                {{else if (eq asset.extension "txt")}}
                  <PhFileTxt @size="24" class="shrink-0 mt-0.5" />
                {{else if (eq asset.extension "zip")}}
                  <PhFileZip @size="24" class="shrink-0 mt-0.5" />
                {{else if (eq asset.extension "jpg")}}
                  <PhFileJpg @size="24" class="shrink-0 mt-0.5" />
                {{else if (eq asset.extension "png")}}
                  <PhFilePng @size="24" class="shrink-0 mt-0.5" />
                {{else if (eq asset.extension "img")}}
                  <PhFileImage @size="24" class="shrink-0 mt-0.5" />
                {{else if (eq asset.extension "csv")}}
                  <PhFileCsv @size="24" class="shrink-0 mt-0.5" />
                {{else if (eq asset.extension "text")}}
                  <PhFileText @size="24" class="shrink-0 mt-0.5" />
                {{else}}
                  <PhFile @size="24" class="shrink-0 mt-0.5" />
                {{/if}}

                <a
                  href={{asset.signedUrl}}
                  target="_blank"
                  rel="noopener noreferrer"
                  class="font-medium break-words min-w-0"
                >
                  {{asset.filename}}
                </a>
              </div>
            </options.td>

            <options.td class="text-sm w-1/4">
              <span class="break-words">
                {{this.classificationString asset.classification}}
              </span>
            </options.td>

            <options.td class="w-1/5">
              <span class="text-sm whitespace-nowrap">
                {{safeFormatDate asset.createdAt month="2-digit" day="2-digit" year="numeric"}}
              </span>
            </options.td>
            <options.td class="w-[60px]">
              <div class="flex justify-end items-center">
                {{#if asset.attachmentDeletedAt}}
                  <Popover @placement="bottom-end" @triggerMode="hover">
                    <:trigger as |pop|>
                      <span class="px-4" {{pop.register}} {{pop.hoverHandlers}}>
                        <PhInfo @size="24" />
                      </span>
                    </:trigger>
                    <:body>
                      <div class="p-2 text-sm">
                        Deleted on
                        {{safeFormatDate
                          asset.attachmentDeletedAt
                          month="2-digit"
                          day="2-digit"
                          year="numeric"
                        }}
                      </div>
                    </:body>
                  </Popover>
                {{else}}
                  <Button
                    @appearance="minimal"
                    @isRunning={{this.removeAsset.isRunning}}
                    class="!flex-nowrap !text-tio-gray-700"
                    data-test-remove-attachment-button
                    disabled={{this.removeAsset.isRunning}}
                    title="Remove Attachment"
                    {{on "click" (fn this.removeAssetAction asset)}}
                  >
                    <PhXCircle @size="24" />
                  </Button>
                {{/if}}
              </div>
            </options.td>
          </options.tr>
        {{/each}}
      </:tbody>
    </Table>
  </template>
}
